import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ConfigProvider, DatePicker, Input, Popover, Select, Switch } from "antd";
import type { Dayjs } from "dayjs";
import { ArrowRight, Upload, FileJson, FileText, File, X, Loader2 } from "lucide-react";
import { YEAR_PRESETS, isCustomYear } from "../../pages/Modus/RoadmapBuilder/timeframe";
import "../../pages/Modus/RoadmapBuilder/rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

type Mode = "build" | "import";

interface ParsedImport {
  companyName: string;
  buildingBlocks: Record<string, unknown>;
  gaps: unknown[];
  outstandingQuestions: unknown[];
}

const ACCEPTED_TYPES = ".json,.pdf,.txt,text/plain,application/json,application/pdf";

function fileIcon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "json") return <FileJson style={{ width: 15, height: 15, color: "#2c1561", flexShrink: 0 }} />;
  if (ext === "pdf")  return <FileText style={{ width: 15, height: 15, color: "#dc2626", flexShrink: 0 }} />;
  return <File style={{ width: 15, height: 15, color: "#64748b", flexShrink: 0 }} />;
}

function ToggleRow({
  label, description, checked, onChange,
}: { label: string; description: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px",
        borderRadius: 8, width: "100%", textAlign: "left",
        background: checked ? "hsl(var(--rb-primary) / 0.04)" : "#f8fafc",
        border: `1px solid ${checked ? "hsl(var(--rb-primary) / 0.2)" : "#e2e8f0"}`,
        cursor: "pointer", transition: "all 0.15s",
      }}
    >
      <Switch
        size="small"
        checked={checked}
        onChange={onChange}
        onClick={(_, e) => e.stopPropagation()}
        style={{ marginTop: 2, flexShrink: 0 }}
      />
      <div>
        <p style={{ fontSize: 12, fontWeight: 600, margin: 0, color: "#0f172a" }}>{label}</p>
        <p style={{ fontSize: 11, color: "#64748b", margin: "2px 0 0", lineHeight: 1.4 }}>{description}</p>
      </div>
    </button>
  );
}

export interface NewRoadmapFormProps {
  /** Called when the user clicks the back / cancel button */
  onBack?: () => void;
}

export function NewRoadmapForm({ onBack }: NewRoadmapFormProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("build");

  // ── Build mode state ──────────────────────────────────────────────────────
  const [companyName, setCompanyName] = useState("");
  const [domain, setDomain] = useState("");
  const [context, setContext] = useState("");
  const [peakDate, setPeakDate] = useState<Dayjs | null>(null);
  const [yearStartMonth, setYearStartMonth] = useState(1);
  const [yearStartDay, setYearStartDay] = useState(1);
  const [yearPopoverOpen, setYearPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buildWithAI, setBuildWithAI] = useState(true);

  // ── Import mode state ─────────────────────────────────────────────────────
  const [organizeWithAI, setOrganizeWithAI] = useState(true);
  const [includeResearch, setIncludeResearch] = useState(false);
  const [importedData, setImportedData] = useState<ParsedImport | null>(null);
  const [rawContent, setRawContent] = useState<{ file?: File; text?: string } | null>(null);
  const [importName, setImportName] = useState("");
  const [pasteValue, setPasteValue] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const applyParsed = (data: ParsedImport) => {
    setParseError(null);
    setImportedData(data);
    setRawContent(null);
    setImportName(data.companyName);
  };

  const clearImport = () => {
    setImportedData(null);
    setRawContent(null);
    setImportName("");
    setPasteValue("");
    setParseError(null);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOrganizeToggle = (val: boolean) => {
    setOrganizeWithAI(val);
    clearImport();
  };

  // ── Build from scratch ────────────────────────────────────────────────────

  const handleStart = async () => {
    if (!companyName.trim()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: companyName.trim(),
          domain: buildWithAI ? domain.trim() : "",
          context: context.trim(),
          peakDate: peakDate ? peakDate.format("YYYY-MM-DD") : null,
          yearStartMonth,
          yearStartDay,
          skipResearch: !buildWithAI,
        }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create session");
      const session = await res.json();
      navigate(`/roadmap/build_roadmap/${session.id}`);
    } catch {
      setIsLoading(false);
    }
  };

  // ── Import: file parsing ──────────────────────────────────────────────────

  const parseFile = useCallback(async (file: File) => {
    setPendingFile(file);
    setParseError(null);

    const ext = file.name.split(".").pop()?.toLowerCase();

    // JSON: always try client-side parse for Vojomo format first
    if (ext === "json") {
      setIsParsing(true);
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.buildingBlocks) {
          applyParsed({
            companyName: data.companyName ?? "Imported Roadmap",
            buildingBlocks: data.buildingBlocks,
            gaps: data.gaps ?? [],
            outstandingQuestions: data.outstandingQuestions ?? [],
          });
          return;
        }
      } catch { /* not valid JSON — fall through */ }
      finally { setIsParsing(false); }
    }

    // Non-Vojomo content: check organize toggle
    if (!organizeWithAI) {
      const suggested = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
      setRawContent({ file });
      setImportName(suggested || "");
      return;
    }

    // Organize ON: server-side AI parse
    setIsParsing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${SERVER_URL}/api/rb/parse-import`, {
        method: "POST", body: form, credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Could not parse file");
      }
      applyParsed(await res.json());
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to parse file";
      setParseError(msg);
      setImportedData(null);
      setPendingFile(null);
    } finally {
      setIsParsing(false);
    }
  }, [organizeWithAI]);

  const parseText = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setParseError(null);

    // Always try JSON parse first
    try {
      const data = JSON.parse(trimmed);
      if (data.buildingBlocks) {
        applyParsed({
          companyName: data.companyName ?? "Imported Roadmap",
          buildingBlocks: data.buildingBlocks,
          gaps: data.gaps ?? [],
          outstandingQuestions: data.outstandingQuestions ?? [],
        });
        return;
      }
    } catch { /* not Vojomo JSON */ }

    // Plain text: check organize toggle
    if (!organizeWithAI) {
      setRawContent({ text: trimmed });
      return;
    }

    // Organize ON: AI parse
    setIsParsing(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/parse-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: trimmed }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Could not interpret the text as a roadmap");
      }
      applyParsed(await res.json());
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : "Failed to parse text");
      setImportedData(null);
    } finally {
      setIsParsing(false);
    }
  }, [organizeWithAI]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  // ── Import: submit ────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!importName.trim()) return;
    if (!importedData && !rawContent) return;
    setIsImporting(true);
    try {
      // 1. Create session (research always controlled separately for import)
      const createRes = await fetch(`${SERVER_URL}/api/rb/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName: importName.trim(), skipResearch: true }),
        credentials: "include",
      });
      if (!createRes.ok) throw new Error("Failed to create session");
      const session = await createRes.json();

      // 2a. Organize ON — import structured building blocks
      if (organizeWithAI && importedData) {
        const importRes = await fetch(`${SERVER_URL}/api/rb/sessions/${session.id}/import`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            buildingBlocks: importedData.buildingBlocks,
            gaps: importedData.gaps,
            outstandingQuestions: importedData.outstandingQuestions,
          }),
          credentials: "include",
        });
        if (!importRes.ok) throw new Error("Failed to import roadmap data");
      }

      // 2b. Organize OFF — store raw content as a data source
      if (!organizeWithAI && rawContent) {
        if (rawContent.file) {
          const form = new FormData();
          form.append("file", rawContent.file);
          await fetch(`${SERVER_URL}/api/rb/sessions/${session.id}/store-content`, {
            method: "POST", body: form, credentials: "include",
          });
        } else if (rawContent.text) {
          await fetch(`${SERVER_URL}/api/rb/sessions/${session.id}/store-content`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: rawContent.text }),
            credentials: "include",
          });
        }
      }

      // 3. Fire research if requested
      if (includeResearch) {
        await fetch(`${SERVER_URL}/api/rb/sessions/${session.id}/refresh-research`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
          credentials: "include",
        });
      }

      navigate(`/roadmap/build_roadmap/${session.id}`);
    } catch {
      setIsImporting(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const hasContent = !!(importedData || rawContent);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      <Card styles={{ body: { padding: 28 } }}>
        {/* Header + mode toggle */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 12px", color: "#0f172a", fontFamily: '"Albert Sans", sans-serif' }}>
            New Roadmap
          </h1>
          <div style={{ display: "flex", gap: 0, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
            {(["build", "import"] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); clearImport(); }}
                style={{
                  padding: "5px 14px", fontSize: 12,
                  fontWeight: mode === m ? 600 : 400,
                  fontFamily: '"Albert Sans", sans-serif',
                  background: mode === m ? "#2c1561" : "transparent",
                  color: mode === m ? "#fff" : "#64748b",
                  border: "none", cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {m === "build" ? "Build from scratch" : "Import"}
              </button>
            ))}
          </div>
        </div>

        {/* ── Build from scratch ── */}
        {mode === "build" && (
          <div className="space-y-4">
            <div className="space-y-1">
              <label style={{ fontSize: 12, fontWeight: 500 }}>
                Roadmap for <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <Input
                size="large"
                placeholder="e.g. Vojomo, Vojomo Book, Product Launch 2026"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onPressEnter={handleStart}
                autoFocus
              />
            </div>

            <ToggleRow
              label="Build initial draft with Vo"
              description={
                buildWithAI
                  ? "Vo will search the web and pre-populate your building blocks from context and research."
                  : "Starts blank — you build the roadmap yourself with Vo in the session."
              }
              checked={buildWithAI}
              onChange={setBuildWithAI}
            />

            {buildWithAI && (
              <>
                <div className="space-y-1">
                  <label style={{ fontSize: 12, fontWeight: 500 }}>
                    URLs{" "}
                    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <Input
                    placeholder="e.g. vojomo.com, linkedin.com/company/vojomo"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                  <p style={{ fontSize: 11, color: "#94a3b8", margin: "3px 0 0" }}>
                    Any web presence — separate multiple URLs with commas
                  </p>
                </div>

                <div className="space-y-1">
                  <label style={{ fontSize: 12, fontWeight: 500 }}>
                    Context{" "}
                    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <Input.TextArea
                    placeholder="Give Vo a head start — describe what this is, what you're trying to achieve, who it's for."
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={4}
                    style={{ resize: "none" }}
                  />
                </div>
              </>
            )}

            {!buildWithAI && (
              <div className="space-y-1">
                <label style={{ fontSize: 12, fontWeight: 500 }}>
                  Background{" "}
                  <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                </label>
                <Input.TextArea
                  placeholder="Share any background with Vo — it'll be available as context when you start working together in the session."
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  rows={3}
                  style={{ resize: "none" }}
                />
              </div>
            )}

            <div className="space-y-1">
              <label style={{ fontSize: 12, fontWeight: 500 }}>
                Target peak date{" "}
                <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
              </label>
              <DatePicker
                picker="month"
                placeholder="e.g. Dec 2030"
                value={peakDate}
                onChange={(val) => setPeakDate(val)}
                style={{ width: "100%" }}
                format="MMM YYYY"
              />
              <p style={{ fontSize: 11, color: "#94a3b8", margin: "3px 0 0" }}>
                When do you want to achieve this? Vo will anchor the roadmap around this date.
              </p>
            </div>

            <Popover
              open={yearPopoverOpen}
              onOpenChange={setYearPopoverOpen}
              trigger="click"
              placement="bottomLeft"
              title={<span style={{ fontSize: 12, fontWeight: 500 }}>Year definition</span>}
              content={
                <div className="space-y-2" style={{ width: 260 }}>
                  <Select
                    style={{ width: "100%" }}
                    size="small"
                    value={
                      isCustomYear({ month: yearStartMonth, day: yearStartDay })
                        ? "custom"
                        : `${yearStartMonth}-${yearStartDay}`
                    }
                    onChange={(val) => {
                      if (val === "custom") return;
                      const [m, d] = val.split("-").map(Number);
                      setYearStartMonth(m);
                      setYearStartDay(d);
                    }}
                    options={[
                      ...YEAR_PRESETS.map((p) => ({ value: `${p.month}-${p.day}`, label: p.label })),
                      { value: "custom", label: "Custom…" },
                    ]}
                  />
                  {isCustomYear({ month: yearStartMonth, day: yearStartDay }) && (
                    <div className="flex items-center gap-2">
                      <Select
                        style={{ flex: 1 }}
                        size="small"
                        value={yearStartMonth}
                        onChange={setYearStartMonth}
                        options={Array.from({ length: 12 }, (_, i) => ({
                          value: i + 1,
                          label: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i],
                        }))}
                      />
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>starts day</span>
                      <Input
                        type="number" min={1} max={28} size="small"
                        value={yearStartDay}
                        onChange={(e) => setYearStartDay(Math.min(28, Math.max(1, Number(e.target.value))))}
                        style={{ width: 52 }}
                      />
                    </div>
                  )}
                  <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
                    Affects how Vo labels quarters and years in your roadmap.
                  </p>
                </div>
              }
            >
              <button
                type="button"
                style={{
                  fontSize: 12, color: "#64748b", background: "none",
                  border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 10px", width: "100%",
                }}
              >
                <span style={{ fontSize: 13 }}>⚙</span>
                <span style={{ fontWeight: 500 }}>Year definition</span>
                {!isCustomYear({ month: yearStartMonth, day: yearStartDay }) && (
                  <span style={{ color: "#cbd5e1" }}>
                    ({YEAR_PRESETS.find((p) => p.month === yearStartMonth && p.day === yearStartDay)?.label ?? "Custom"})
                  </span>
                )}
              </button>
            </Popover>

            <Button
              type="primary" block size="large"
              style={{ marginTop: 8 }}
              onClick={handleStart}
              disabled={!companyName.trim() || isLoading}
            >
              {isLoading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "spin 0.6s linear infinite" }} />
                  Starting session...
                </span>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {buildWithAI ? "Build a draft roadmap" : "Create blank roadmap"}
                  <ArrowRight style={{ width: 14, height: 14 }} />
                </span>
              )}
            </Button>

            {onBack && (
              <Button type="text" block onClick={onBack} style={{ color: "#64748b", fontSize: 12 }}>
                ← Back
              </Button>
            )}
          </div>
        )}

        {/* ── Import ── */}
        {mode === "import" && (
          <div className="space-y-4">
            <ToggleRow
              label="Let Vo organize into building blocks"
              description={
                organizeWithAI
                  ? "Vo will map your content to the Vojomo roadmap framework automatically."
                  : "Your content is stored as-is. Work with Vo in the session to structure it on your terms."
              }
              checked={organizeWithAI}
              onChange={handleOrganizeToggle}
            />
            <ToggleRow
              label="Search for external context"
              description={
                includeResearch
                  ? "Vo will search the web for current information about your organization after importing."
                  : "Uses only the content you provide — no external data added."
              }
              checked={includeResearch}
              onChange={setIncludeResearch}
            />

            <div style={{ height: 1, background: "#f1f5f9" }} />

            {!hasContent ? (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>
                    Upload a file
                  </label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => !isParsing && fileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${isDragging ? "#2c1561" : "#e2e8f0"}`,
                      borderRadius: 10, padding: "24px 20px", textAlign: "center",
                      cursor: isParsing ? "default" : "pointer",
                      background: isDragging ? "#f5f3ff" : "#fafbff",
                      transition: "all 0.15s",
                    }}
                  >
                    {isParsing && pendingFile ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <Loader2 style={{ width: 22, height: 22, color: "#2c1561" }} className="animate-spin" />
                        <p style={{ fontSize: 13, color: "#2c1561", margin: 0, fontWeight: 500 }}>
                          Reading {pendingFile.name}…
                        </p>
                        <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
                          {organizeWithAI ? "Extracting roadmap structure" : "Preparing file"}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload style={{ width: 22, height: 22, color: isDragging ? "#2c1561" : "#94a3b8", margin: "0 auto 8px" }} />
                        <p style={{ fontSize: 13, color: isDragging ? "#2c1561" : "#475569", margin: "0 0 3px", fontWeight: 500 }}>
                          Drop a file here
                        </p>
                        <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
                          or click to browse — supports <strong>.json</strong>, <strong>.pdf</strong>, or <strong>.txt</strong>
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) parseFile(file);
                      e.target.value = "";
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 1, background: "#f1f5f9" }} />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>or paste content</span>
                  <div style={{ flex: 1, height: 1, background: "#f1f5f9" }} />
                </div>

                <div className="space-y-2">
                  <Input.TextArea
                    rows={5}
                    placeholder={
                      organizeWithAI
                        ? "Paste a roadmap export, notes, or any description — Vo will extract the structure."
                        : "Paste any content — it will be stored as-is and available when you work with Vo."
                    }
                    value={pasteValue}
                    onChange={(e) => { setPasteValue(e.target.value); setParseError(null); }}
                    style={{ resize: "none", fontSize: 12 }}
                    disabled={isParsing}
                  />
                  <Button
                    block
                    loading={isParsing && !pendingFile}
                    onClick={() => organizeWithAI ? parseText(pasteValue) : (setRawContent({ text: pasteValue.trim() }))}
                    disabled={!pasteValue.trim() || isParsing}
                    style={{ fontSize: 12 }}
                  >
                    {isParsing && !pendingFile
                      ? "Extracting roadmap…"
                      : organizeWithAI ? "Extract Roadmap" : "Use This Content"}
                  </Button>
                </div>

                {parseError && (
                  <p style={{ fontSize: 12, color: "#ef4444", margin: 0 }}>{parseError}</p>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", borderRadius: 8,
                  background: "#f0fdf4", border: "1px solid #bbf7d0",
                }}>
                  {pendingFile
                    ? fileIcon(pendingFile.name)
                    : rawContent?.file
                      ? fileIcon(rawContent.file.name)
                      : <FileText style={{ width: 15, height: 15, color: "#16a34a", flexShrink: 0 }} />
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 500, color: "#166534", margin: 0 }}>
                      {importedData ? "Roadmap structure extracted" : "Content ready"}
                    </p>
                    <p style={{ fontSize: 11, color: "#166534", margin: 0 }}>
                      {importedData
                        ? `${Object.keys(importedData.buildingBlocks).length} blocks found${pendingFile ? ` from ${pendingFile.name}` : ""}`
                        : rawContent?.file
                          ? `${rawContent.file.name} — will be stored as reference`
                          : `${rawContent?.text?.length ?? 0} characters — will be stored as reference`
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearImport}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex" }}
                  >
                    <X style={{ width: 14, height: 14 }} />
                  </button>
                </div>

                <div className="space-y-1">
                  <label style={{ fontSize: 12, fontWeight: 500 }}>
                    Roadmap name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Input
                    size="large"
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="Name your roadmap"
                    autoFocus
                  />
                  {importedData && (
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: "3px 0 0" }}>
                      Pre-filled from the source — edit if needed
                    </p>
                  )}
                </div>

                <Button
                  type="primary" block size="large"
                  onClick={handleImport}
                  disabled={!importName.trim() || isImporting}
                >
                  {isImporting ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "spin 0.6s linear infinite" }} />
                      Creating roadmap…
                    </span>
                  ) : (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {organizeWithAI ? "Import Roadmap" : "Create Roadmap"}
                      <ArrowRight style={{ width: 14, height: 14 }} />
                    </span>
                  )}
                </Button>
              </div>
            )}

            {onBack && !hasContent && (
              <Button type="text" block onClick={onBack} style={{ color: "#64748b", fontSize: 12 }}>
                ← Back
              </Button>
            )}
          </div>
        )}
      </Card>
    </ConfigProvider>
  );
}
